import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useParams } from "react-router";
import CombinedOutputPage from "./CombinedOutputPage";
import RunGpSummaryStageDetails from "./run-stage/RunGpSummaryStageDetails";
import RunStageDetails from "./run-stage/RunStageDetails";
import useRunResultWithStages, { type RunResultWithStages } from "./useRunResultWithStages";
import HrgDisplay from "./HrgDisplay";

type StageKey =
  | Extract<keyof RunResultWithStages, "endoscopyStageResult" | "histologyStageResult" | "gpSummaryStageResult">
  | "combinedOutput";

const STAGE_KEYS: { key: StageKey; label: string }[] = [
  { key: "endoscopyStageResult", label: "Endoscopy" },
  { key: "histologyStageResult", label: "Histology" },
  { key: "gpSummaryStageResult", label: "GP Summary" },
  { key: "combinedOutput", label: "Combined Output" },
];

const renderStage = (runResult: RunResultWithStages, stageKey: StageKey) => {
  switch (stageKey) {
    case "gpSummaryStageResult":
      return <RunGpSummaryStageDetails run={runResult} stageKey={stageKey} stageResult={runResult[stageKey]} />;
    case "endoscopyStageResult":
    case "histologyStageResult":
      return <RunStageDetails run={runResult} stageKey={stageKey} stageResult={runResult[stageKey]!} />;
    default:
      return <CombinedOutputPage runId={runResult.id} />;
  }
};

const RunDetailsPage = () => {
  const { runId } = useParams();
  const { data: runResult, ...queryResult } = useRunResultWithStages({ runId });

  if (queryResult.isLoading) {
    return <div>Loading...</div>;
  }

  if (queryResult.error) {
    return <div>Error: {queryResult.error.message}</div>;
  }

  if (!runResult) {
    return <div>No result found.</div>;
  }

  // Find which stages are present
  const presentStages = STAGE_KEYS.filter(({ key }) => runResult[key as keyof typeof runResult]);

  if (runResult.testEncounterResult) {
    presentStages.push({ key: "combinedOutput", label: "Combined Output" });
  }

  if (presentStages.length === 0) {
    return <div>No stage results available.</div>;
  }

  // If only one stage, show it directly
  if (presentStages.length === 1) {
    const { key } = presentStages[0];

    return renderStage(runResult, key);
  }

  // Otherwise, show tabs
  return (
    <div className="w-full">
      <Tabs defaultValue={presentStages[0].key} className="w-full">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="flex-1">
            {presentStages.map(({ key, label }) => (
              <TabsTrigger key={key} value={key}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* HRG Section - positioned alongside tabs */}
          {runResult.testEncounterResult && (
            <div className="ml-4">
              <HrgDisplay hrgs={runResult.testEncounterResult.test_run_hrg} showEmptyState={true} />
            </div>
          )}
        </div>

        {/* Tab content */}
        {presentStages.map(({ key }) => (
          <TabsContent key={key} value={key}>
            {renderStage(runResult, key)}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default RunDetailsPage;
